import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { Edit, Search, Visibility } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";

import MainContainer from "../../components/MainContainer";
import MetricsStrip from "../../components/MetricsStrip";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ForbiddenPage from "../../components/ForbiddenPage";
import KnowledgeBaseModal from "../../components/KnowledgeBaseModal";
import KnowledgeBaseDetail from "../../components/KnowledgeBaseDetail";
import AiCredentialsModal from "../../components/AiCredentialsModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { DEFAULT_KNOWLEDGE_LIMITS, statusInfo } from "../../helpers/knowledgeManagement";

const useStyles = makeStyles((theme) => ({
  main: { minWidth: 0 },
  stack: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
  hero: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: theme.spacing(2), [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" } },
  title: { fontWeight: 600 },
  tabsPaper: { padding: theme.spacing(0.75), borderRadius: 18, border: `1px solid ${theme.palette.divider}`, boxShadow: "none" },
  tab: { minHeight: 40, borderRadius: 12, textTransform: "none", fontWeight: 600, "&.Mui-selected": { backgroundColor: theme.palette.action.hover } },
  search: { padding: theme.spacing(1.5), borderRadius: 18, border: `1px solid ${theme.palette.divider}`, boxShadow: "none" },
  table: { borderRadius: 18, border: `1px solid ${theme.palette.divider}`, overflow: "hidden", boxShadow: "none" },
  name: { fontWeight: 600 },
  chips: { display: "flex", gap: theme.spacing(0.5), justifyContent: "center", flexWrap: "wrap" },
  empty: { padding: theme.spacing(5), textAlign: "center" },
}));

const KnowledgeBases = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const linkedBaseId = new URLSearchParams(location.search).get("baseId");
  const { user } = useContext(AuthContext);
  const [bases, setBases] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [limits, setLimits] = useState(DEFAULT_KNOWLEDGE_LIMITS);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [baseModal, setBaseModal] = useState({ open: false, base: null });
  const [selectedBase, setSelectedBase] = useState(null);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [resumeBaseAfterCredentials, setResumeBaseAfterCredentials] = useState(false);
  const [credentialDefaults, setCredentialDefaults] = useState({ purpose: "embedding" });

  useEffect(() => {
    if (!linkedBaseId || user.profile === "user") return undefined;
    let active = true;
    api.get(`/knowledge-bases/${encodeURIComponent(linkedBaseId)}`)
      .then(({ data }) => { if (active) setSelectedBase(data); })
      .catch(error => { if (active) toastError(error); });
    return () => { active = false; };
  }, [linkedBaseId, user.profile]);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/ai/embedding-profiles"),
      api.get("/ai/credentials"),
      api.get("/knowledge/management-settings"),
    ])
      .then(([profileResponse, credentialResponse, settingsResponse]) => {
        if (!active) return;
        setProfiles(profileResponse.data.profiles || []);
        setCredentials(credentialResponse.data || []);
        setLimits({ ...DEFAULT_KNOWLEDGE_LIMITS, ...(settingsResponse.data.limits || {}) });
      })
      .catch(toastError);
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/knowledge-bases", {
          params: { searchParam: search, pageNumber: page + 1, pageSize: rowsPerPage },
        });
        if (active) {
          setBases(data.knowledgeBases || []);
          setCount(data.count || 0);
        }
      } catch (error) {
        if (active) toastError(error);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [page, reload, rowsPerPage, search]);

  const metrics = useMemo(() => [
    { key: "bases", label: "Bases", value: count },
    { key: "sources", label: "Fontes nesta página", value: bases.reduce((sum, base) => sum + Number(base.sourceCount || 0), 0) },
    { key: "active", label: "Fontes em uso", value: bases.reduce((sum, base) => sum + Number(base.activeSourceCount || 0), 0) },
    { key: "agents", label: "Vínculos com agentes", value: bases.reduce((sum, base) => sum + Number(base.agentCount || 0), 0) },
  ], [bases, count]);

  const refresh = () => setReload((value) => value + 1);
  const closeBaseModal = () => setBaseModal({ open: false, base: null });
  const openCredentialsFromBase = (defaults) => {
    setCredentialDefaults(defaults || { purpose: "embedding" });
    setResumeBaseAfterCredentials(true);
    setBaseModal((current) => ({ ...current, open: false }));
    setCredentialsModalOpen(true);
  };
  const closeCredentialsModal = () => {
    setCredentialsModalOpen(false);
    if (resumeBaseAfterCredentials) {
      setBaseModal((current) => ({ ...current, open: true }));
      setResumeBaseAfterCredentials(false);
    }
  };
  const saved = (base) => {
    closeBaseModal();
    if (selectedBase?.id === base.id) setSelectedBase(base);
    refresh();
  };

  if (user.profile === "user") {
    return <MainContainer className={classes.main} fullWidth><ForbiddenPage /></MainContainer>;
  }

  return (
    <MainContainer className={classes.main} fullWidth>
      <KnowledgeBaseModal
        open={baseModal.open}
        onClose={closeBaseModal}
        onSaved={saved}
        onConfigureCredentials={openCredentialsFromBase}
        knowledgeBase={baseModal.base}
        profiles={profiles}
        credentials={credentials}
      />
      <AiCredentialsModal
        open={credentialsModalOpen}
        onClose={closeCredentialsModal}
        onSaved={setCredentials}
        initialPurpose={credentialDefaults.purpose}
        initialProvider={credentialDefaults.provider}
      />
      <KnowledgeBaseDetail
        open={Boolean(selectedBase)}
        onClose={() => setSelectedBase(null)}
        knowledgeBase={selectedBase}
        onEdit={(base) => setBaseModal({ open: true, base })}
        onChanged={refresh}
        limits={limits}
        profiles={profiles}
        credentials={credentials}
      />

      <div className={classes.stack}>
        <div className={classes.hero}>
          <Box>
            <Typography variant="h5" className={classes.title}>Bases de conhecimento</Typography>
            <Typography variant="body2" color="textSecondary">
              Organize fontes privadas reutilizáveis. Nada é usado em respostas antes do processamento e da aprovação.
            </Typography>
          </Box>
          <Box display="flex" gridGap={8}>
            <Button variant="outlined" onClick={() => {
              setResumeBaseAfterCredentials(false);
              setCredentialDefaults({ purpose: "embedding" });
              setCredentialsModalOpen(true);
            }}>Credenciais de IA</Button>
            <Button variant="outlined" onClick={refresh} disabled={loading}>Atualizar</Button>
            <Button color="primary" variant="contained" onClick={() => setBaseModal({ open: true, base: null })}>Nova base</Button>
          </Box>
        </div>

        <Paper className={classes.tabsPaper}>
          <Tabs value="knowledge" onChange={(_event, value) => history.push(value === "library" ? "/prompts?section=library" : value === "values" ? "/prompts?section=values" : value === "agents" ? "/prompts" : value === "usage" ? "/ai-usage" : "/knowledge-bases")} indicatorColor="primary" variant="scrollable" scrollButtons="auto">
            <Tab className={classes.tab} value="agents" label="Meus agentes" />
            <Tab className={classes.tab} value="library" label="Biblioteca de templates" />
            <Tab className={classes.tab} value="values" label="Variáveis da empresa" />
            <Tab className={classes.tab} value="knowledge" label="Bases de conhecimento" />
            <Tab className={classes.tab} value="usage" label="Consumo e saúde" />
          </Tabs>
        </Paper>

        <MetricsStrip ariaLabel="Indicadores de bases de conhecimento" items={metrics} />

        <Paper className={classes.search}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar bases por nome, descrição ou finalidade"
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(0); }}
            inputProps={{ "aria-label": "Pesquisar bases de conhecimento" }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search color="action" /></InputAdornment> }}
          />
        </Paper>

        <Paper className={classes.table}>
          <TableContainer>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Base</TableCell>
                <TableCell>Perfil de embeddings</TableCell>
                <TableCell align="center">Fontes</TableCell>
                <TableCell align="center">Agentes</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {bases.map((base) => (
                  <TableRow key={base.id} hover>
                    <TableCell>
                      <Typography variant="body2" className={classes.name}>{base.name}</Typography>
                      <Typography variant="caption" color="textSecondary">{base.description || "Sem descrição"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{base.embeddingProfile?.code || "—"}</Typography>
                      <Typography variant="caption" color="textSecondary">{base.embeddingProfile?.dimension || 0} dimensões · {base.credential?.provider || "sem credencial"}</Typography>
                    </TableCell>
                    <TableCell align="center"><div className={classes.chips}><Chip size="small" label={`${base.sourceCount || 0} total`} /><Chip size="small" color="primary" label={`${base.activeSourceCount || 0} em uso`} /></div></TableCell>
                    <TableCell align="center">{base.agentCount || 0}</TableCell>
                    <TableCell align="center"><Chip size="small" variant="outlined" label={base.reindexStatus ? `Reindexação: ${statusInfo(base.reindexStatus).label}` : (base.status === "active" ? "Ativa" : "Arquivada")} /></TableCell>
                    <TableCell align="center">
                      <IconButton size="small" aria-label={`Abrir ${base.name}`} onClick={() => setSelectedBase(base)}><Visibility /></IconButton>
                      <IconButton size="small" aria-label={`Editar ${base.name}`} onClick={() => setBaseModal({ open: true, base })}><Edit /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={6} />}
              </TableBody>
            </Table>
          </TableContainer>
          {!loading && !bases.length && (
            <Box className={classes.empty}>
              <Typography variant="h6">Nenhuma base encontrada</Typography>
              <Typography variant="body2" color="textSecondary">Crie uma base e escolha um perfil homologado de embeddings.</Typography>
            </Box>
          )}
          <TablePagination
            component="div"
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_event, value) => setPage(value)}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[20, 40, 50]}
            labelRowsPerPage="Por página"
          />
        </Paper>
      </div>
    </MainContainer>
  );
};

export default KnowledgeBases;
